import { core } from "@tauri-apps/api";

import { debugSystem } from "@/domains/logging/utils/debugLog";

let currentConfig: WorkspaceConfig = {};

/**
 * Normalisiert die Workspace-Daten aus dem rohen Tauri-Response.
 *
 * @param raw - Rohe Daten vom Tauri-Command
 *
 * @returns Normalisierte Workspace-Config
 *
 */
function normalizeWorkspaceConfig(raw: Record<string, unknown>): WorkspaceConfig {
  const normalized: WorkspaceConfig = {};

  for (const [wsKey, wsValue] of Object.entries(raw)) {
    if (!wsValue || typeof wsValue !== "object") {
      continue;
    }
    const ws = wsKey.trim();
    const normalizedAspects: WorkspaceEntry = {};

    for (const [aspKey, aspValue] of Object.entries(wsValue as Record<string, unknown>)) {
      if (!aspValue || typeof aspValue !== "object") {
        continue;
      }
      const asp = aspKey.trim();
      normalizedAspects[asp] = aspValue as AspectConfig;
    }

    normalized[ws] = normalizedAspects;
  }

  return normalized;
}

/**
 * Lädt die Workspace-Config aus Supabase über Tauri.
 * Die Config wird jetzt über eine Supabase-Tabelle verwaltet.
 *
 * @returns Promise das void resolved wenn die Config geladen wurde
 *
 */
export async function loadConfig(): Promise<void> {
  try {
    // Tauri verfügbar?
    const isTauri = "__TAURI_IPC__" in globalThis;

    if (isTauri) {
      const raw = await core.invoke<unknown>("load_workspace_config");

      if (!raw || typeof raw !== "object") {
        // Ungültige Struktur – sichere Fallback-Config
        setConfig({});
        return;
      }

      const normalized = normalizeWorkspaceConfig(raw as Record<string, unknown>);

      validateConfigColors(normalized);
      setConfig(normalized);
    } else {
      // Development-Modus: Leere Config verwenden
      // Die Config wird über Supabase geladen, wenn Tauri verfügbar ist
      setConfig({});
    }
  } catch (error) {
    // Fehler beim Laden der Config - leere Config verwenden
    // Error is intentionally swallowed as empty config is expected fallback
    debugSystem("debug", "loadConfig failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    setConfig({});
  }
}

/**
 * Gibt die aktuelle Workspace-Config zurück.
 *
 * @returns Die aktuelle Workspace-Config
 *
 */
export function getConfig(): WorkspaceConfig {
  return currentConfig;
}

/**
 * Setzt die aktuelle Workspace-Config zur Laufzeit.
 *
 * @param newConfig - Neue Config die gesetzt werden soll
 *
 */
export function setConfig(newConfig: WorkspaceConfig): void {
  currentConfig = newConfig;
}

/**
 * Gibt eine Liste verfügbarer Workspaces zurück.
 *
 * @returns Array von Workspace-Optionen mit label und value
 *
 */
export function getWorkspaceOptions(): { label: string; value: string }[] {
  return Object.keys(currentConfig).map((ws) => ({
    label: ws.replace(/^\d+\.\s*/, ""),
    value: ws,
  }));
}

/**
 * Gibt alle Aspects eines Workspace zurück.
 *
 * @param workspace - Der Workspace-Name
 *
 * @returns Array von Aspect-Optionen mit label und value
 *
 */
export function getAspectOptions(workspace: string): { label: string; value: string }[] {
  const workspaceEntry = currentConfig[workspace];
  if (!workspaceEntry) {
    return [];
  }
  // Optimiertes Regex: Verwendet [^\]] statt .*? für bessere Performance und verhindert Backtracking
  return Object.keys(workspaceEntry).map((asp) => {
    // Reason: Regex is safe here as input is controlled (workspace aspect names from config, not user input).
    // Using [^\]] instead of .*? for better performance and to prevent backtracking.
    // The regex matches optional whitespace, followed by [brackets with non-bracket chars], at end of string.
    // eslint-disable-next-line sonarjs/slow-regex -- Reason: Input is controlled (workspace aspect names from config, not user input). Regex uses [^\]] to prevent backtracking and matches only at end of string. Input length is bounded by config structure.
    const label = asp.replace(/\s*\[[^\]]*\]$/, "");
    return {
      label,
      value: asp,
    };
  });
}

/**
 * Normalisiert einen String für Unicode-sicheren Vergleich.
 *
 * @param input - Der zu normalisierende String
 *
 * @returns Normalisierter String
 *
 */
function normalizeString(input: string): string {
  return input.trim().toLowerCase().normalize("NFKC");
}

/**
 * Gibt die konfigurierte Farbe eines Workspace-Aspects zurück – robust gegen Unicode, Case & Whitespace.
 *
 * @param config - Die Workspace-Config
 *
 * @param workspace - Der Workspace-Name
 *
 * @param aspect - Der Aspect-Name
 *
 * @returns Die konfigurierte Farbe oder undefined
 *
 */
export function getSafeAspectColor(
  config: WorkspaceConfig,
  workspace: string,
  aspect: string,
): string | undefined {
  const ws = normalizeString(workspace);
  const asp = normalizeString(aspect);

  const wsEntry = Object.entries(config).find(([key]) => normalizeString(key) === ws);

  if (!wsEntry) {
    return undefined;
  }

  const [, workspaceEntry] = wsEntry;

  for (const [key, value] of Object.entries(workspaceEntry)) {
    if (normalizeString(key) === asp) {
      return value.color;
    }
  }

  return undefined;
}

/**
 * Validiert alle Aspekte auf gültige Farben (#RRGGBB).
 *
 * @param config - Die Workspace-Config die validiert werden soll
 *
 */
export function validateConfigColors(config: WorkspaceConfig): void {
  const validHex = /^#[0-9A-F]{6}$/i;

  for (const aspects of Object.values(config)) {
    for (const data of Object.values(aspects)) {
      const { color } = data;
      if (!color || !validHex.test(color)) {
        // NOTE: Invalid color format - validation would log error in production
      }
    }
  }
}

// Typen
export interface AspectConfig {
  carryover: boolean;
  color?: string;
  count_hours: boolean;
  count_plan: boolean;
  hours: number;
  status: "Inactive" | "Include";
}

export type WorkspaceEntry = Record<string, AspectConfig>;
export type WorkspaceConfig = Record<string, WorkspaceEntry>;
